import React, { useContext, useEffect, useLayoutEffect, useState } from "react";
import ViewportContext from "../../../../context/studyviewer/ViewportContext";
import Viewport from "./Viewport";
import MPRViewport from "./MPRViewport";
import Volume3DViewport from "./Volume3DViewport";
import { isMobile } from "react-device-detect";
import { VIEWPORT_COLUMN, VIEWPORT_ROW } from "../../../../constants/Constants";
import { resizeViewport, performResize, resizeViewportsAfterLayout, resizeViewportsImmediate } from "../../helpers/CornerstoneViewportService";
import $ from "jquery";
import studyviewerContext from "../../../../context/studyviewer/StudyviewerContext";
import ThumbnailContext from "../../../../context/studyviewer/ThumbnailContext";
import themeContext from "../../../../context/theme/ThemeContext";

const ViewportLayout = ({ priors,initialVOI }) => {
  const {
    viewportRow,
    viewportColumn,
    activeViewportIndex,
    isMPRActive,
    activeMPRViewportIndex,
    isSagittalActive,
    setIsSagittalActive,
    isAxialActive,
    setIsAxialActive,
    isCoronalActive,
    setIsCoronalActive,
    activeTool,
    viewportSeriesData,
    viewportDoubleClickIndex,
    is3DActive,
    viewportImageLoaded,
  } = useContext(ViewportContext);

  const { studyParentId, viewerOpacity, isLandscape } = useContext(studyviewerContext);
  const isPortrait = !isMobile && !isLandscape;
  const { getThemeColor, themeName } = useContext(themeContext);
  const activeViewportBorderColor = themeName === "light" ? "#0044bb" : "#00aaff";

  const [rows] = useState(() => {
    let array = [];
    for (let i = 0; i < VIEWPORT_ROW; i++) {
      array.push(i);
    }
    return array;
  });
  const [columns] = useState(() => {
    let array = [];
    for (let i = 0; i < VIEWPORT_COLUMN; i++) {
      array.push(i);
    }
    return array;
  });

  useEffect(() => {

  }, [viewportRow, viewportColumn]);

  // After MPR exits the DOM reverts from position:absolute → position:static,
  // which changes the containing block for the absolutely-positioned inner
  // viewport containers.  The resize that fires inside the changeTool handler
  // runs BEFORE this React re-render, so canvases are sized against the stale
  // MPR layout.
  //
  // Correct them before the browser paints, so the normal viewer never shows a
  // frame of MPR-sized canvases stretched into their full-size boxes.
  useLayoutEffect(() => {
    if (!isMPRActive) {
      resizeViewportsImmediate();
    }
  }, [isMPRActive]);

  // Settle pass for the above: the stack viewports may still be re-attaching
  // their images when the layout effect runs.
  useEffect(() => {
    if (!isMPRActive) {
      resizeViewport();
    }
  }, [isMPRActive]);

  // The MPR panes are absolutely positioned with percentage sizes and have NO CSS
  // transition, so maximizing one (double-click) or switching plane changes the
  // PANE size in a single frame — the parent #viewport-main-container never
  // changes size.
  //
  // The cornerstone canvas is styled width/height:100%, but its BITMAP size is
  // only updated while a render runs, and cornerstone defers every render to a
  // requestAnimationFrame.  Anything that resizes on a rAF (or from a passive
  // useEffect, which runs after paint) therefore leaves at least one painted
  // frame in which the old, smaller bitmap is stretched across the new, 3x wider
  // box — the momentary stretch seen on double-click.
  //
  // useLayoutEffect runs after React commits the new pane sizes but BEFORE the
  // browser paints, and resizeViewportsImmediate() also forces the render
  // synchronously, so the first painted frame is already correct.
  useLayoutEffect(() => {
    if (!isMPRActive) return;
    resizeViewportsImmediate();
  }, [isMPRActive, isAxialActive, isSagittalActive, isCoronalActive, is3DActive]);

  // Settle pass: on MPR entry the panes/volume may not be ready during the
  // layout effect above, and a late-arriving volume load can land after it.
  // These extra frames are no-ops once the canvases match their boxes.
  useEffect(() => {
    if (!isMPRActive) return;
    resizeViewportsAfterLayout();
  }, [isMPRActive, isAxialActive, isSagittalActive, isCoronalActive, is3DActive]);

  // ResizeObserver: makes Cornerstone canvas pixel dimensions track the
  // viewport container size on every frame during CSS panel animations
  // (sidebar slide-in, and the 0.3s height/width/top/left transition on
  // .viewport-layout-inner-container that animates a normal-viewer
  // double-click maximize). Without this, the canvas stays at stale dimensions
  // for the full 300ms CSS transition, then snaps — visible as a jump.
  useEffect(() => {
    if (isMobile) return;
    const container = document.getElementById('viewport-main-container');
    if (!container) return;

    let debounceId = null;

    const observer = new ResizeObserver(() => {
      // Resize AND redraw synchronously, right here in the observer callback.
      // ResizeObserver callbacks run after layout but BEFORE the frame is
      // painted, so the canvas bitmap matches the box in the very frame the new
      // box is first shown.  Deferring this to a requestAnimationFrame (and
      // letting cornerstone defer its render to yet another frame) left the
      // canvas ~2 frames behind the animating box for the whole transition —
      // the stale bitmap being stretched to fit is what read as a blurry
      // "stretch" while the viewport grew.
      // resizeViewportsImmediate() no-ops when every canvas already matches its
      // displayed size, so the extra callbacks a single frame can deliver are
      // free, and it cannot feed itself into a ResizeObserver loop.
      resizeViewportsImmediate();
      // After the animation settles, do one precise resize with pan/zoom preservation
      clearTimeout(debounceId);
      debounceId = setTimeout(() => {
        performResize();
      }, 200);
    });

    observer.observe(container);
    // Also observe individual viewport containers so the observer fires
    // during double-click CSS transitions (parent size doesn't change,
    // only child sizes animate).  The MPR panes are included for the same
    // reason: maximizing one plane resizes only the panes themselves.
    const viewportContainers = container.querySelectorAll(
      '.viewport-layout-inner-container, .viewport-layout-mpr-viewport'
    );
    viewportContainers.forEach((el) => observer.observe(el));

    return () => {
      observer.disconnect();
      clearTimeout(debounceId);
    };
    // Re-subscribe when the MPR panes mount/unmount — they do not exist on the
    // first run of this effect — and when the normal-viewer grid changes, which
    // is the only thing that adds/removes .viewport-layout-inner-container
    // elements.  Without the row/column deps, viewports added by a layout change
    // are never observed and stop tracking their box.
  }, [isMPRActive, is3DActive, viewportRow, viewportColumn]);

  // Derived during render, not in an effect: as state they lagged a render
  // behind viewportRow/viewportColumn, so a layout change (a hanging protocol
  // going from 1x1 to 1x3, say) rendered its new viewport boxes at the old
  // full width for one frame, and Cornerstone sized its canvases — and drew the
  // images — against that stale width before the grid caught up.
  const height = viewportRow === 1 ? 100 : 100 / viewportRow;
  const width = viewportColumn === 1 ? 100 : 100 / viewportColumn;

  // Resize Cornerstone canvases synchronously after the DOM updates with new
  // height/width but BEFORE the browser paints.  This prevents the one-frame
  // stretch that occurs when the container dimensions change but the canvas
  // still has the old pixel size.
  //
  // resizeViewportSync() only queued the redraw that actually resizes the
  // on-screen canvas bitmap, so it still left one stretched frame; the
  // "Immediate" variant does that redraw before the paint.  Layout changes that
  // animate (the 0.3s transition on .viewport-layout-inner-container) are a
  // no-op here — the box has not moved yet — and are carried by the
  // ResizeObserver below instead.
  useLayoutEffect(() => {
    if (!isMobile && height !== undefined && width !== undefined) {
      resizeViewportsImmediate();
    }
  }, [height, width]);

  useEffect(() => {
    const viewportContainer = $(`#viewport-box-${activeViewportIndex}`);
    viewportContainer.addClass("blinking");
    setTimeout(() => {
      viewportContainer.removeClass("blinking");
    }, 1000);
  }, [activeViewportIndex, activeTool]);

  const isPortraitMode = !isMobile && window.innerHeight > window.innerWidth;

  return (
    <>
      <div
        className="viewport-layout-container"
        // style={{
        //   display: `${!isMPRActive ? "block" : "none"}`,
        //   height: `${isMobile ? "100%" : "98%"}`,
        // }}
        style={{
            position: isMPRActive ? "absolute" : "static",
            opacity: isMPRActive  ? 0 : 1,
            pointerEvents: isMPRActive ? "none" : "auto",
            zIndex: isMPRActive ? -1 : "auto",
            height: "100%",
          }}
      >
        {/* VIEWPORT 1 */}
        {rows.length > 0 &&
          columns.length > 0 &&
          rows.map((i) =>
            columns.map((j, index) => {
              return (
                <React.Fragment key={index}>
                  {i < viewportRow && j < viewportColumn && (
                    <div
                      className={`viewport-layout-inner-container ${(
                        viewportSeriesData[i * VIEWPORT_COLUMN + j]?.study != null &&
                        String(viewportSeriesData[i * VIEWPORT_COLUMN + j]?.study) === studyParentId && priors &&
                        // a slot re-added by a layout change keeps its stale series entry but shows no image
                        viewportImageLoaded[i * VIEWPORT_COLUMN + j]
                      ) ? "active-viewport-top-border" : ""}`}
                      style={{
                        // display: `${
                        //    ? "block" : "none"
                        // }`,
                        height:  viewportDoubleClickIndex === -1 || viewportDoubleClickIndex == null
                        ? `calc(${height}% - ${4 / viewportRow}px)`
                        : 'calc(100% - 4px)',

                        width:  viewportDoubleClickIndex === -1 || viewportDoubleClickIndex == null
                        ? `calc(${width}% - ${4 / viewportColumn}px)`
                        : 'calc(100% - 4px)',

                        top: viewportDoubleClickIndex === -1 || viewportDoubleClickIndex == null
                          ? `calc(${i * height}% + 2px)`
                          : "2px",

                        left: viewportDoubleClickIndex === -1 || viewportDoubleClickIndex == null
                          ? `calc(${j * width}% + 2px)`
                          : "2px",
                        opacity: viewportDoubleClickIndex === -1 || viewportDoubleClickIndex == null
                          ? 1
                          : viewportDoubleClickIndex === i * VIEWPORT_COLUMN + j
                          ? 1
                          : 0,
                        pointerEvents: viewportDoubleClickIndex === -1 || viewportDoubleClickIndex == null
                          ? "auto"
                          : viewportDoubleClickIndex === i * VIEWPORT_COLUMN + j
                          ? "auto"
                          : "none",
                        zIndex: viewportDoubleClickIndex === i * VIEWPORT_COLUMN + j
                          ? 1
                          : 0,
                      }}
                    >
                      <div
                        id={`viewport-box-${i * VIEWPORT_COLUMN + j}`}
                        className={`viewport-layout-viewport h100 w100`}
                        style={{
                          border: activeViewportIndex === i * VIEWPORT_COLUMN + j
                            ? `2px solid ${activeViewportBorderColor}`
                            : "2px solid rgba(128,128,128,0.3)",
                          backgroundColor: 'black',
                        }}
                      >
                        <div className="h100 w100" style={{
                          opacity: `${viewerOpacity}`,
                        }}>
                          <Viewport viewportIndex={i * VIEWPORT_COLUMN + j} priors={priors} setInitialVOI={initialVOI}/>
                        </div>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })
          )}
      </div>

      {/* MPR VOLUME VIEWPORTS */}
      {isMPRActive && (
        <div
          className="viewport-layout-mpr-viewport-container w100"
          style={{
            display: `${isMPRActive ? "block" : "none"}`,
          }}
        >
          {/* AXIAL VIEWPORT */}
          <div
            className="viewport-layout-mpr-viewport"
            style={{
              visibility: `${
                isMPRActive && !is3DActive && (isSagittalActive || isCoronalActive)
                  ? "hidden"
                  : ""
              }`,
              height: is3DActive
                ? (isMobile ? "32%" : "48.5%")
                : (isMobile ? "98%" : isPortrait ? (isAxialActive ? "98%" : "32%") : "98%"),
              width: is3DActive
                ? (isMobile ? "100%" : "49.5%")
                : (isMobile ? "100%" : isPortrait ? "99%" : isAxialActive ? "99%" : "32.66%"),
              top: is3DActive
                ? (isMobile ? "1%" : "0.5%")
                : (isMobile ? "1%" : "1%"),
              left: is3DActive
                ? (isMobile ? "0.5%" : "0.25%")
                : (isMobile ? "0.5%" : "0.5%"),
            }}
          >
            <div
              onDoubleClick={() => {
                if (!is3DActive) {
                  // The canvases are resized by the useLayoutEffect above, which
                  // runs once React has committed the new pane size and before
                  // the browser paints it.  Resizing from here would run against
                  // the OLD, uncommitted layout.
                  setIsAxialActive(!isAxialActive);
                }
              }}
              className="viewport-layout-viewport h100 w100"
              style={{
                borderStyle: "solid",
                borderColor: `${
                  activeMPRViewportIndex === 0
                    ? getThemeColor("active-viewport")
                    : "rgb(255,255,255,0.3)"
                }`,
                borderWidth: activeMPRViewportIndex === 0 ? "1.5px" : "1px",
              }}
            >
              <MPRViewport orientation={"axial"} viewportIndex={0} setInitialVOI={initialVOI} />
            </div>
          </div>
          {/* SAGITTAL VIEWPORT */}
          <div
            className="viewport-layout-mpr-viewport"
            style={{
              visibility: `${
                isMPRActive && !is3DActive && (isAxialActive || isCoronalActive)
                  ? "hidden"
                  : ""
              }`,
              width: is3DActive
                ? (isMobile ? "100%" : "49.5%")
                : (isMobile ? "100%" : isPortrait ? "99%" : isSagittalActive ? "99%" : "32.66%"),
              height: is3DActive
                ? (isMobile ? "32%" : "48.5%")
                : (isMobile ? "98%" : isPortrait ? (isSagittalActive ? "98%" : "32%") : "98%"),
              top: is3DActive
                ? (isMobile ? "34%" : "0.5%")
                : (isMobile ? "1%" : isPortrait ? (isSagittalActive ? "1%" : "34%") : "1%"),
              left: is3DActive
                ? (isMobile ? "0.5%" : "50.25%")
                : (isMobile ? "0.5%" : isPortrait ? "0.5%" : isSagittalActive ? "0.5%" : "33.66%"),
            }}
          >
            <div
              onDoubleClick={() => {
                if (!is3DActive) {
                  // See the axial pane above — the resize is done pre-paint by
                  // the useLayoutEffect, not from this handler.
                  setIsSagittalActive(!isSagittalActive);
                }
              }}
              className="viewport-layout-viewport h100 w100"
              style={{
                borderStyle: "solid",
                borderColor: `${
                  activeMPRViewportIndex === 1
                    ? getThemeColor("active-viewport")
                    : "rgb(255,255,255,0.3)"
                }`,
                borderWidth: activeMPRViewportIndex === 1 ? "1.5px" : "1px",
              }}
            >
              <MPRViewport orientation={"sagittal"} viewportIndex={1}  setInitialVOI={initialVOI}/>
            </div>
          </div>
          {/* CORONAL VIEWPORT */}
          <div
            className="viewport-layout-mpr-viewport"
            style={{
              visibility: `${
                isMPRActive && !is3DActive && (isAxialActive || isSagittalActive)
                  ? "hidden"
                  : ""
              }`,
              width: is3DActive
                ? (isMobile ? "100%" : "49.5%")
                : (isMobile ? "100%" : isPortrait ? "99%" : isCoronalActive ? "99%" : "32.66%"),
              height: is3DActive
                ? (isMobile ? "32%" : "48.5%")
                : (isMobile ? "98%" : isPortrait ? (isCoronalActive ? "98%" : "32%") : "98%"),
              top: is3DActive
                ? (isMobile ? "67%" : "50%")
                : (isMobile ? "1%" : isPortrait ? (isCoronalActive ? "1%" : "67%") : "1%"),
              left: is3DActive
                ? (isMobile ? "0.5%" : "0.25%")
                : (isMobile ? "0.5%" : isPortrait ? "0.5%" : isCoronalActive ? "0.5%" : "66.82%"),
            }}
          >
            <div
              onDoubleClick={() => {
                if (!is3DActive) {
                  // See the axial pane above — the resize is done pre-paint by
                  // the useLayoutEffect, not from this handler.
                  setIsCoronalActive(!isCoronalActive);
                }
              }}
              className="viewport-layout-viewport h100 w100"
              style={{
                borderStyle: "solid",
                borderColor: `${
                  activeMPRViewportIndex === 2
                    ? getThemeColor("active-viewport")
                    : "rgb(255,255,255,0.3)"
                }`,
                borderWidth: activeMPRViewportIndex === 2 ? "1.5px" : "1px",
              }}
            >
              <MPRViewport orientation={"coronal"} viewportIndex={2} setInitialVOI={initialVOI}/>
            </div>
          </div>
          {/* 3D VOLUME VIEWPORT */}
          {is3DActive && (
            <div
              className="viewport-layout-mpr-viewport"
              style={{
                width: isMobile ? "100%" : "49.5%",
                height: isMobile ? "32%" : "48.5%",
                top: isMobile ? "100%" : "50%",
                left: isMobile ? "0.5%" : "50.25%",
              }}
            >
              <div
                className="viewport-layout-viewport h100 w100"
                style={{
                  borderStyle: "solid",
                  borderColor: `${
                    activeMPRViewportIndex === 3
                      ? getThemeColor("active-viewport")
                      : "rgb(255,255,255,0.3)"
                  }`,
                  borderWidth: activeMPRViewportIndex === 3 ? "1.5px" : "1px",
                }}
              >
                <Volume3DViewport />
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ViewportLayout;
